import React, { useState } from 'react';
import { useTranslation } from '@/context/TranslationContext';
import { Lock, ShieldCheck, AlertCircle } from 'lucide-react';
import { useInviteGate } from '@/components/invite-gate/useInviteGate';

/** InviteGate — wraps an audience-specific public page and gates it behind an
 * admin-issued invite code. All unlock logic lives in useInviteGate. */
export default function InviteGate({ audience, children }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const { unlocked, checking, autoUnlocking, error, submitting, submit } = useInviteGate(audience);

  if (unlocked) return children;

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(code);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] flex items-center justify-center px-6 py-16" data-testid={`invite-gate-${audience}`}>
      <div className="max-w-md w-full">
        <div className="rounded-2xl border border-[var(--border-hover)] bg-[var(--bg-subtle)] p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#825743]/10 flex items-center justify-center">
              <Lock className="w-4 h-4 text-[var(--accent)]" aria-hidden="true" />
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#825743]/80">{t('Invite only')}</p>
              <h1 className="font-heading text-2xl text-[var(--text-primary)]">{t('Enter your access code')}</h1>
            </div>
          </div>

          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-6">
            {t('This page is shared privately. Please enter the access code sent to you by the Sahiri team. Access is remembered for this browser session only.')}
          </p>

          {(checking || autoUnlocking) ? (
            <div className="text-center py-4 text-xs text-[var(--text-secondary)]" data-testid={`invite-gate-${audience}-checking`}>
              {autoUnlocking ? t('Verifying your link…') : t('Checking access…')}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]/80 mb-2 block">{t('Access code')}</span>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={t('e.g. ABC123XYZ')}
                  autoFocus
                  required
                  className="w-full px-4 py-3 rounded-lg bg-[var(--bg-subtle)] border border-[var(--border-hover)] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/40 focus:border-[#825743]/40 focus:outline-none focus:ring-1 focus:ring-[#825743]/30 tracking-widest font-mono text-sm"
                  data-testid={`invite-gate-${audience}-input`}
                />
              </label>

              {error && (
                <div className="flex items-start gap-2 text-xs text-red-400" data-testid={`invite-gate-${audience}-error`}>
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !code.trim()}
                className="w-full py-3 rounded-full bg-[var(--accent)] text-white text-sm font-medium hover:bg-[var(--accent-hover)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`invite-gate-${audience}-submit`}
              >
                {submitting ? t('Verifying…') : t('Unlock page')}
              </button>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-[var(--border)] space-y-3">
            <p className="text-[11px] text-[var(--text-secondary)]/70 leading-relaxed flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-[#825743]/60 shrink-0 mt-0.5" />
              {t("Don't have a code?")} <a href="/contact" className="text-[var(--accent)] hover:underline ml-1">{t('Contact the Sahiri team')}</a>.
            </p>
            <p className="text-[11px] text-[var(--text-secondary)]/50">
              <a href="/" className="hover:text-[var(--text-primary)] transition-colors">{t('← Back to Sahiri Amali')}</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
